import { Asset } from '../../../store/Asset';
import { assetCommissionsStatusOptions } from '../../../store/GeneralStore';
import { DetailPanelParams, FieldLabelMap } from '../../Compounds/DetailPanel/DetailPanel';
import { EditFieldType } from '../../Compounds/DetailPanel/LabelValueField';
import {
  CommissionsValidationsSubStatusOptions,
  LegacyArchiveReason,
  DisconnectedReason
} from '../../../utils/constants';

export const FIELD_LABEL_MAP: FieldLabelMap = {
  id: 'ID',
  name: 'Name',
  sf_id: 'SFID',
  accountId: 'Account ID',
  supplierId: 'Supplier ID',
  customer: 'Customer',
  tsd: 'TSD',
  supplierName: 'Supplier',
  salesAgent: 'Sales Agent',
  directSales: 'Account Direct Sales',
  latestSalesAssignmentDate: 'Latest Sales Assignment Date',
  directSalesPaid: 'Direct Sales Paid',
  directSalesPaidPercent: 'Direct Sales Paid %',
  commissionsExperienceAnalyst: 'Commissions Experience Analyst',
  customerSuccessManager: 'CSM',
  contract: 'Contract',
  serviceStatus: 'Service Status',
  product: 'Product',
  contractSignDate: 'Contract Sign Date',
  termLength: 'Term Length',
  installDate: 'Install Date',
  location: 'Location',
  supplierOrderNumber: 'Supplier Order Number',
  accountNumber: 'Account Number',
  activationDate: 'Activation Date',
  expectedRpmAccountNumber: 'Expected RPM Account Number',
  billingCommencementDate: 'Billing Commencement Date',
  tsdTrackingNumber: 'TSD Tracking Number',
  upcomingRenewalDate: 'Upcoming Renewal Date',
  circuitId: 'Circuit ID',
  cancelledDate: 'Cancelled Date',
  disconnectDate: 'Disconnect Date',
  disconnectedReason: 'Disconnect Reason',
  expectedFirstCommissionRun: 'Expected First Commission Run',
  actualFirstCommissionRun: 'Actual First Commission Run',
  actualFirstSpiffRun: 'Actual First SPIFF Run',
  commissionableMRC: 'Commissionable MRC',
  grossMRR: 'Gross MRR',
  totalMRC: 'Total MRC',
  commissionableNRC: 'Commissionable NRC',
  grossNRR: 'Gross NRR',
  commissionsStatus: 'Commissions Status',
  assetStatus: 'Service Status',
  expectedDecommissionDate: 'Expected Decommission Run',
  actualDecommissionedDate: 'Actual Decommission Run',
  complimentaryBillingMonths: 'Complimentary Billing Months',
  locationName: 'Location Name',
  locationAddress: 'Location Address',
  recurringCommissionPercent: 'Recurring Commission %',
  nonRecurringCommissionPercent: 'Non Recurring Commission %',
  netMRR: 'Net MRR',
  netNRR: 'Net NRR',
  nonCommissionableNRC: 'Non Commissionable NRC',
  estimatedDeliveryDate: 'Estimated Delivery Date',
  contractNumber: 'Contract',
  commissionsValidatedDate: 'Commissions Validated Date',
  commissionsValidationStatus: 'Commissions Validation Status',
  commissionsValidationSubStatus: 'Commissions Validation Sub Status',
  legacyArchiveReason: 'Legacy Archive Reason',
  decommissionReason: 'Decommission Reason',
  decommissionNotes: 'Decommission Notes',
  split1SubAgent: 'Split 1 Sub-Agent',
  split1Rate: 'Split 1 Rate',
  split2SubAgent: 'Split 2 Sub-Agent',
  split2Rate: 'Split 2 Rate',
  x2Split: '2.0 Split',
  x2Rate: '2.0 Rate',
  lineType: 'Line Type',
  quoteType: 'Quote Type',
  replacedAssetNumber: 'Replaced By',
  replacementOfAssetNumber: 'Replacement Of',
  notesToCommissions: 'Notes to Commissions',
  description: 'Description',
  blendedRate: 'Is Blended Rate?',
  paymentFrequency: 'Payment Frequency',
  expectedVariableComponents: 'Expected Variable Components',
  dateAutoRenewed: 'Date Auto Renewed',
  cancelledReason: 'Cancelled Reason'
};

export const assetSectionsInfo = (data: Asset): DetailPanelParams[] => {
  const isDecommissioned = data.commissionsStatus === 'Decommissioned';
  return [
    {
      data,
      canEdit: [],
      fieldMap: FIELD_LABEL_MAP,
      order: ['customer', 'salesAgent', 'supplierName', 'tsd'],
      links: [{ field: 'customer', link: `/customer/${data.accountGri}`, external: false }]
    },
    {
      data,
      canEdit: [],
      fieldMap: FIELD_LABEL_MAP,
      order: ['quoteType', 'lineType', 'replacementOfAssetNumber', 'replacedAssetNumber'],
      links: [
        {
          field: 'replacementOfAssetNumber',
          link: `/asset/${data.replacementOfAssetID}`,
          // eslint-disable-next-line max-len
          externalLink: `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Asset/${data.replacementOfAssetSFID}/view `,
          external: true
        },
        {
          field: 'replacedAssetNumber',
          link: `/asset/${data.replacedAssetID}`,
          // eslint-disable-next-line max-len
          externalLink: `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Asset/${data.replacedAssetSFID}/view `,
          external: true
        }
      ]
    },
    {
      data,
      canEdit: [],
      fieldMap: FIELD_LABEL_MAP,
      order: ['commissionsExperienceAnalyst', 'customerSuccessManager']
    },
    {
      data,
      canEdit: [],
      fieldMap: FIELD_LABEL_MAP,
      links: [],
      order: ['description']
    },
    {
      data,
      canEdit: [],
      fieldMap: FIELD_LABEL_MAP,
      order: ['locationName', 'locationAddress']
    },
    {
      data,
      canEdit: [
        { field: 'expectedRpmAccountNumber', type: EditFieldType.TEXT },
        { field: 'tsdTrackingNumber', type: EditFieldType.TEXT },
        { field: 'billingCommencementDate', type: EditFieldType.DATE },
        ...(isDecommissioned
          ? [
              { field: 'disconnectDate', type: EditFieldType.DATE },
              {
                field: 'disconnectedReason',
                type: EditFieldType.SELECT,
                options: [null, ...Object.values(DisconnectedReason)]
              }
            ]
          : [])
      ],
      fieldMap: FIELD_LABEL_MAP,
      links: [],
      order: [
        'product',
        'contractSignDate',
        'termLength',
        'estimatedDeliveryDate',
        'supplierOrderNumber',
        'installDate',
        'accountNumber',
        'activationDate',
        'expectedRpmAccountNumber',
        'billingCommencementDate',
        'tsdTrackingNumber',
        'upcomingRenewalDate',
        'circuitId',
        'dateAutoRenewed',
        'complimentaryBillingMonths',
        'expectedVariableComponents',
        'disconnectDate',
        'cancelledDate',
        'disconnectedReason',
        'cancelledReason'
      ]
    },
    {
      data,
      canEdit: [],
      fieldMap: FIELD_LABEL_MAP,
      links: [],
      order: ['notesToCommissions']
    }
  ];
};

export const commissionsSectionInfo = (data: Asset): DetailPanelParams[] => [
  {
    data,
    canEdit: [
      {
        field: 'commissionsStatus',
        type: EditFieldType.SELECT,
        options: [null, ...assetCommissionsStatusOptions]
      },
      {
        field: 'actualFirstCommissionRun',
        type: EditFieldType.DATE
      },
      {
        field: 'actualDecommissionedDate',
        type: EditFieldType.DATE
      },
      {
        field: 'actualFirstSpiffRun',
        type: EditFieldType.DATE
      }
    ],
    fieldMap: FIELD_LABEL_MAP,
    order: [
      'commissionsStatus',
      'assetStatus',
      'expectedFirstCommissionRun',
      'actualFirstCommissionRun',
      'expectedDecommissionDate',
      'actualDecommissionedDate',
      '',
      'actualFirstSpiffRun'
    ]
  },
  {
    data,
    canEdit: [
      { field: 'commissionableMRC', type: EditFieldType.CURRENCY },
      { field: 'totalMRC', type: EditFieldType.CURRENCY },
      { field: 'grossMRR', type: EditFieldType.CURRENCY },
      { field: 'recurringCommissionPercent', type: EditFieldType.PERCENT }
    ],
    fieldMap: FIELD_LABEL_MAP,
    order: ['totalMRC', 'commissionableMRC', 'recurringCommissionPercent', 'grossMRR']
  },
  {
    data,
    canEdit: [
      { field: 'nonCommissionableNRC', type: EditFieldType.CURRENCY },
      { field: 'commissionableNRC', type: EditFieldType.CURRENCY },
      { field: 'grossNRR', type: EditFieldType.CURRENCY },
      { field: 'nonRecurringCommissionPercent', type: EditFieldType.PERCENT }
    ],
    fieldMap: FIELD_LABEL_MAP,
    order: ['nonCommissionableNRC', 'commissionableNRC', 'nonRecurringCommissionPercent', 'grossNRR']
  },
  {
    data,
    canEdit: [
      { field: 'netMRR', type: EditFieldType.CURRENCY },
      { field: 'netNRR', type: EditFieldType.CURRENCY }
    ],
    fieldMap: FIELD_LABEL_MAP,
    order: ['netMRR', 'netNRR']
  }
];

export const assetValidationInfo = (data: Asset): DetailPanelParams[] => {
  const isLegacyArchive = data.commissionsStatus === 'Legacy Archive';
  const isDecommissioned = data.commissionsStatus === 'Decommissioned';

  const canEdit = [
    {
      field: 'commissionsValidatedDate',
      type: EditFieldType.DATE
    },
    {
      field: 'commissionsValidationStatus',
      type: EditFieldType.SELECT,
      options: [null, 'Validated', 'Could Not Validate', 'Partially Validated']
    },
    {
      field: 'commissionsValidationSubStatus',
      type: EditFieldType.MULTI_SELECT,
      options: CommissionsValidationsSubStatusOptions
    },
    {
      field: 'blendedRate',
      type: EditFieldType.CHECKBOX
    },
    ...(isLegacyArchive
      ? [
          {
            field: 'legacyArchiveReason',
            type: EditFieldType.SELECT,
            options: [null, ...Object.values(LegacyArchiveReason)]
          }
        ]
      : []),
    {
      field: 'paymentFrequency',
      type: EditFieldType.SELECT,
      options: [null]
    }
  ];

  const order = [
    'commissionsValidationStatus',
    'commissionsValidationSubStatus',
    'commissionsValidatedDate',
    'blendedRate',
    ...(isLegacyArchive ? ['legacyArchiveReason'] : []),
    ...(isDecommissioned ? ['decommissionReason', 'decommissionNotes'] : []),
    'paymentFrequency'
  ];

  return [{ data, canEdit, fieldMap: FIELD_LABEL_MAP, order }];
};

export const assetRpmAccountsInfo = (data: Asset): DetailPanelParams[] => [
  {
    data,
    canEdit: [],
    fieldMap: FIELD_LABEL_MAP,
    order: []
  }
];

export const assetPayoutInfo = (data: Asset): DetailPanelParams[] => [
  {
    data,
    canEdit: [],
    fieldMap: FIELD_LABEL_MAP,
    order: ['directSales', 'latestSalesAssignmentDate', 'directSalesPaid', 'directSalesPaidPercent']
  },
  {
    data,
    canEdit: [],
    fieldMap: FIELD_LABEL_MAP,
    links: [],
    order: ['split1SubAgent', 'split1Rate', 'split2SubAgent', 'split2Rate', 'x2Split', 'x2Rate']
  }
];

export const currencyFields: (keyof Asset)[] = [
  'commissionableMRC',
  'grossMRR',
  'totalMRC',
  'commissionableNRC',
  'grossNRR',
  'nonCommissionableNRC',
  'netMRR',
  'netNRR'
];

export const percentFields: (keyof Asset)[] = [
  'nonRecurringCommissionPercent',
  'recurringCommissionPercent',
  'split1Rate',
  'split2Rate',
  'x2Rate',
  'directSalesPaidPercent'
];

export const financialEditFields = [
  'commissionableMRC',
  'totalMRC',
  'recurringCommissionPercent',
  'grossMRR',
  'commissionableNRC',
  'nonCommissionableNRC',
  'nonRecurringCommissionPercent',
  'grossNRR',
  'netMRR',
  'netNRR',
  'commissionableMrc',
  'commissionableNrc',
  'nonCommissionableNrc',
  'expectedGrossMrr',
  'expectedGrossNrr',
  'expectedNetMrr',
  'expectedNetNrr',
  'nonRecurringCommission',
  'recurringCommission',
  'mrc'
];
